import { inject, Injectable } from "@angular/core";
import { CheckoutSessionsService, PaymentSchedulingService } from "../../../api/services";
import {
  CreateCheckoutSessionDto,
  CreatePackageCheckoutSessionDto,
  ResponseCheckoutSessionDto,
  UpdatePaymentSchedulingDto,
} from "../../../api/models";
import { Observable } from "rxjs";
import { CheckoutSessionsControllerCreateCheckoutSession$Params } from "../../../api/fn/checkout-sessions/checkout-sessions-controller-create-checkout-session";
import { CheckoutSessionsControllerCreatePackageCheckoutSession$Params } from "../../../api/fn/checkout-sessions/checkout-sessions-controller-create-package-checkout-session";
import { environment } from "../../../environments/environment";
import { LoggedUserService } from "@keus-template/auth";

@Injectable({
  providedIn: "root",
})
export class StripeApiService {
  private readonly _checkoutSessionsService: CheckoutSessionsService = inject(
    CheckoutSessionsService,
  );
  /** Injected service for logged user */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);
  /** Injected payment scheduling service (generated from OpenAPI) */
  private readonly _paymentSchedulingService: PaymentSchedulingService = inject(
    PaymentSchedulingService,
  );

  public createCheckoutSession$(
    doctorId: string,
    visitId: string,
    bookingDate: string,
    isPsychologist?: boolean,
  ): Observable<ResponseCheckoutSessionDto> {
    const user = this._loggedUserService.currentUser();
    const customerId = user.stripeId;
    const username = user.email;
    const patientId = user.id;
    const body: CreateCheckoutSessionDto = {
      tenant: environment.tenant,
      priceId: isPsychologist ? environment.stripePsychoPriceId : environment.stripePriceId,
      quantity: 1,
      mode: "setup",
      cancelUrl: this.getCancelUrl(),
      successUrl: this.getSuccessUrl(),
      customerId,
      doctorId,
      visitId,
      username,
      patientId,
      bookingDate,
    };
    const params: CheckoutSessionsControllerCreateCheckoutSession$Params = {
      body,
    };
    return this._checkoutSessionsService.checkoutSessionsControllerCreateCheckoutSession(
      params,
    );
  }

  /**
   * Gets the cancel URL based on the current environment
   */
  private getCancelUrl(): string {
    const baseUrl = this.getBaseUrl();
    return `${baseUrl}/my-consultations?success=false`;
  }

  /**
   * Gets the success URL based on the current environment
   */
  private getSuccessUrl(): string {
    const baseUrl = this.getBaseUrl();
    return `${baseUrl}/my-consultations?success=true`;
  }

  /**
   * Updates payment scheduling for visit rescheduling
   * This bypasses Stripe when the payment method is already saved
   * @param visitId - The visit identifier
   * @param bookingDate - The new booking date in ISO format
   * @returns Observable that completes when scheduling is updated
   */
  public updatePaymentScheduling$(
    visitId: string,
    bookingDate: string
  ): Observable<void> {
    const user = this._loggedUserService.currentUser();
    
    if (!user?.tenant) {
      throw new Error("User tenant not found");
    }

    const updateDto: UpdatePaymentSchedulingDto = { bookingDate };

    return this._paymentSchedulingService.paymentSchedulingControllerUpdatePaymentScheduling({
      tenant: user.tenant,
      visitId,
      body: updateDto
    });
  }

  /**
   * Creates a checkout session for Fertility Check-in package payment
   * @param pathwayId - The pathway identifier  
   * @param packageType - Package type (fertility-checkin-male or fertility-checkin-female)
   * @returns Observable with checkout session response
   */
  public createPackageCheckoutSession$(
    pathwayId: string,
    packageType: 'fertility-checkin-male' | 'fertility-checkin-female'
  ): Observable<ResponseCheckoutSessionDto> {
    const user = this._loggedUserService.currentUser();
    const customerId = user.stripeId;
    const username = user.email;
    const patientId = user.id;

    const body: CreatePackageCheckoutSessionDto = {
      tenant: environment.tenant,
      pathwayId,
      patientId,
      packageType,
      customerId,
      username,
    };

    const params: CheckoutSessionsControllerCreatePackageCheckoutSession$Params = {
      body,
    };

    return this._checkoutSessionsService.checkoutSessionsControllerCreatePackageCheckoutSession(
      params,
    );
  }


  /**
   * Gets the base URL based on the current environment
   */
  private getBaseUrl(): string {
    const protocol = window.location.protocol; // 'http:' o 'https:'
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    
    return `${protocol}//${hostname}${port}`;
  }
}